import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { queryFnGET } from "api/tanStackQuery/helpers";
import {
  SharedGridPadding,
  SharedGridStyleNoPadding,
} from "features/CenterContent/shared/MainContentGrid";
import { TrainGpsResponse } from "features/CenterContent/shared/types";
import { trainGpsDataToBaseGpsData } from "features/CenterContent/shared/utils";
import { GPSContent } from "features/CenterContent/VehicleDetails/shared/GPSContent";
import { HeadedContainerSticky } from "features/CenterContent/VehicleDetails/shared/HeadedContainerSticky";
import { FC } from "react";
import { useParams } from "react-router-dom";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import { Box, Flex, Text } from "shared/components/Spor";

type TrainGPSProps = {
  sticky?: boolean;
};

export const TrainGPS: FC<TrainGPSProps> = ({ sticky }) => {
  // @ts-ignore
  const { trainNumber, trainDate, countryCode } = useParams();

  const { status, data: trainGps } = useQuery({
    queryKey: ["trainGps", countryCode, trainNumber, trainDate],
    refetchInterval: 60000,
    queryFn: ({ signal }) =>
      queryFnGET<TrainGpsResponse>({
        signal,
        url: `${getBackendUrl()}/trainGps/gpsDataForTrain/${countryCode}/${trainDate}/${trainNumber}`,
      }),
  });

  const getGPS = () => {
    switch (status) {
      case "pending":
        return <SkeletonLoader skeletonType="trainmap" />;
      case "success":
        if (trainGps.data.length > 0) {
          return (
            <Box aspectRatio="16/9">
              <GPSContent gpsData={trainGpsDataToBaseGpsData(trainGps.data)} />
            </Box>
          );
        }
        return (
          <Flex h="100%">
            <NoDataMessage reason="Det finnes ikke GPS-data for dette toget." />
          </Flex>
        );
      case "error":
        return (
          <FailureMessage customMessage="Kunne ikke hente GPS-data for dette toget" />
        );
    }
  };

  if (!sticky) return getGPS();

  return (
    <HeadedContainerSticky
      panelStyle={SharedGridStyleNoPadding}
      variant="main"
      headerHeight="54px"
      header={
        <Text fontWeight={600} variant="xs" mr="auto" p={SharedGridPadding}>
          GPS
        </Text>
      }
    >
      {getGPS()}
    </HeadedContainerSticky>
  );
};
